import { Link } from "react-router-dom";
import { Show } from "@clerk/react";
import { Sparkles, Trophy, Upload, GraduationCap, Heart, Zap } from "lucide-react";
import Nav from "@/components/Nav";
import Mascot from "@/components/Mascot";

const Feature = ({ icon: Icon, title, body, bg }) => (
  <div className="sticker-card p-6" style={{ background: bg }}>
    <div className="w-12 h-12 rounded-full bg-white border-[3px] border-[#374151] flex items-center justify-center mb-4">
      <Icon strokeWidth={2.5} />
    </div>
    <h3 className="text-xl font-black mb-1 tracking-tight">{title}</h3>
    <p className="text-slate-700">{body}</p>
  </div>
);

export default function Landing() {
  return (
    <>
      <Nav />
      <main className="max-w-6xl mx-auto px-4 pt-10 pb-20">
        {/* HERO */}
        <section className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border-[2px] border-[#374151] bg-[#FFF4C8] font-bold text-sm mb-6">
              <Sparkles size={14} strokeWidth={2.5} /> Study. Play. Beat the clock-less quiz!
            </div>
            <h1 className="text-5xl md:text-6xl font-black tracking-tight leading-[0.95]">
              Learning that<br />
              <span className="text-[var(--tp-primary)]">rolls</span> with you.
            </h1>
            <p className="mt-5 text-lg text-slate-700 max-w-md">
              TP is a quiz game for curious kids and competitive adults.
              Pick a category, keep your 10 daily lives, and climb the leaderboard — no timers, just fun.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Show when="signed-out">
                <Link to="/sign-up" data-testid="hero-cta-signup"
                  className="px-6 py-3 rounded-full border-[3px] border-[#374151] bg-[var(--tp-success)] text-white font-black shadow-[0_4px_0_0_#374151] active:translate-y-1 active:shadow-none">
                  Start playing free
                </Link>
                <Link to="/sign-in" data-testid="hero-cta-signin"
                  className="px-6 py-3 rounded-full border-[3px] border-[#374151] bg-white font-black shadow-[0_4px_0_0_#374151] active:translate-y-1 active:shadow-none">
                  I already have an account
                </Link>
              </Show>
              <Show when="signed-in">
                <Link to="/play" data-testid="hero-cta-play"
                  className="px-6 py-3 rounded-full border-[3px] border-[#374151] bg-[var(--tp-success)] text-white font-black shadow-[0_4px_0_0_#374151] active:translate-y-1 active:shadow-none">
                  Go to Play Dashboard
                </Link>
              </Show>
            </div>
            <p className="mt-4 text-sm text-slate-500">10 free lives every day. Need more? Watch a quick ad.</p>
          </div>

          <div className="flex justify-center md:justify-end">
            <div className="relative">
              <Mascot size={260} />
              <div className="absolute -top-4 -right-4 px-3 py-1 rounded-full border-[3px] border-[#374151] bg-[var(--tp-accent)] font-black text-sm shadow-[0_3px_0_0_#374151] rotate-6">
                Roll with me!
              </div>
              <div className="absolute -bottom-3 -left-6 px-3 py-1 rounded-full border-[3px] border-[#374151] bg-white font-bold text-sm shadow-[0_3px_0_0_#374151] -rotate-6">
                No timers • Just fun
              </div>
            </div>
          </div>
        </section>

        {/* FEATURES */}
        <section className="mt-20">
          <h2 className="text-3xl font-black tracking-tight mb-8">Why kids & grown-ups love TP</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Feature icon={Heart} title="10 lives a day" body="Enough to study without pressure. Watch a tiny ad to earn an extra life." bg="#FFE8EC" />
            <Feature icon={Trophy} title="Leaderboards" body="Celebrate top scorers after every quiz, globally and per quiz." bg="#E6F7FF" />
            <Feature icon={Upload} title="Contribute content" body="Teachers & students can submit question sets. Admin curates and publishes them." bg="#EBFBD6" />
            <Feature icon={GraduationCap} title="Study made fun" body="Turn your lesson materials into quizzes you'll actually want to replay." bg="#FFF4C8" />
            <Feature icon={Zap} title="Multiple choice + True/False" body="Simple formats so even little kids can play. Big tap targets, chunky buttons." bg="#F5EEFF" />
            <Feature icon={Sparkles} title="No timers, no stress" body="Learn at your pace. The right answer is the real reward." bg="#FFF0E6" />
          </div>
        </section>

        {/* FOOTER CTA */}
        <section className="mt-20 sticker-card p-10 bg-white flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-3xl font-black tracking-tight">Ready to roll?</h3>
            <p className="text-slate-700 mt-2">Create a free account in seconds — email or Google.</p>
          </div>
          <Show when="signed-out">
            <Link to="/sign-up" data-testid="footer-cta"
              className="px-6 py-3 rounded-full border-[3px] border-[#374151] bg-[var(--tp-primary)] text-white font-black shadow-[0_4px_0_0_#374151] active:translate-y-1 active:shadow-none">
              Create free account
            </Link>
          </Show>
          <Show when="signed-in">
            <Link to="/play" data-testid="footer-cta-play"
              className="px-6 py-3 rounded-full border-[3px] border-[#374151] bg-[var(--tp-primary)] text-white font-black shadow-[0_4px_0_0_#374151] active:translate-y-1 active:shadow-none">
              Play now
            </Link>
          </Show>
        </section>
        <footer className="mt-12 flex flex-wrap gap-4 text-sm font-bold text-slate-600" data-testid="site-footer">
          <Link to="/privacy" className="hover:underline" data-testid="footer-privacy">Privacy</Link>
          <Link to="/terms" className="hover:underline" data-testid="footer-terms">Terms</Link>
          <Link to="/delete-account" className="hover:underline" data-testid="footer-delete-account">Delete account</Link>
          <span>For ages 13+, and for classrooms with a teacher.</span>
        </footer>
      </main>
    </>
  );
}
